/**
 * product-switcher.js
 * Fast AJAX product switching — fetches the destination page HTML,
 * swaps <main> content in-place, avoids full page reload.
 * Handles both .product-switcher and .pack-switcher components.
 * Falls back to normal navigation on error.
 */
(function () {
  'use strict';

  /* ── Shared state (persists across script re-executions after AJAX swap) ── */
  if (!window.__switcherState) {
    window.__switcherState = {
      cache: {},       // url → { html, doc, title, timestamp }
      fetching: {},    // url → Promise
      version: 0       // race-condition guard
    };
  }
  var S = window.__switcherState;
  var CACHE_TTL = 5 * 60 * 1000; // 5 min — avoids stale prices

  /* ── Fetch + cache a URL ── */

  function fetchPage(url, priority) {
    if (S.cache[url] && (Date.now() - S.cache[url].timestamp < CACHE_TTL)) {
      return Promise.resolve(S.cache[url]);
    }
    if (S.fetching[url]) return S.fetching[url].then(function () { return S.cache[url]; });

    var opts = { credentials: 'same-origin', headers: { 'Accept': 'text/html' } };
    if (priority) opts.priority = priority;

    var p = fetch(url, opts)
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        S.cache[url] = { html: html, doc: doc, title: doc.title, timestamp: Date.now() };
        delete S.fetching[url];
        preloadHeroImage(doc);
        return S.cache[url];
      })
      .catch(function (e) {
        delete S.fetching[url];
        throw e;
      });

    S.fetching[url] = p;
    return p;
  }

  /* ── Preload the main product image from parsed doc ── */

  function preloadHeroImage(doc) {
    var img = doc.querySelector('.sec__main-product .product__media img')
           || doc.querySelector('.sec__main-product img')
           || doc.querySelector('#MainContent img');
    if (!img) return;
    var src = img.getAttribute('src') || img.getAttribute('srcset');
    if (!src) return;
    if (src.indexOf(',') !== -1) src = src.split(',')[0].split(' ')[0];
    var preload = new Image();
    preload.src = src.trim();
  }

  /* ── Collect all switcher URLs (product-switcher + pack-switcher) ── */

  function getSwitcherUrls() {
    var urls = [];
    document.querySelectorAll(
      '.product-switcher__pill[href], .pack-switcher__link[href]'
    ).forEach(function (a) {
      var href = a.getAttribute('href');
      if (href && urls.indexOf(href) === -1) urls.push(href);
    });
    return urls;
  }

  /* ── Activate <script> tags after innerHTML swap ── */

  function activateScripts(container) {
    var scripts = container.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      if (old.type === 'application/json' || old.type === 'application/ld+json'
          || old.type === 'importmap') continue;
      var clone = document.createElement('script');
      for (var a = 0; a < old.attributes.length; a++) {
        clone.setAttribute(old.attributes[a].name, old.attributes[a].value);
      }
      if (old.textContent) clone.textContent = old.textContent;
      old.parentNode.replaceChild(clone, old);
    }
  }

  /* ── Move new <link rel="stylesheet"> / <style> to <head> before swap ── */

  function hoistStylesheets(newMainEl) {
    var links = newMainEl.querySelectorAll('link[rel="stylesheet"]');
    links.forEach(function (link) {
      var href = link.getAttribute('href');
      if (href && !document.querySelector('head link[href="' + CSS.escape(href) + '"]')) {
        var clone = link.cloneNode(true);
        document.head.appendChild(clone);
      }
    });
  }

  /* ── Post-swap: re-init gallery, lazy images, and Swiper ──
   *
   * Custom element constructors fire during innerHTML parse BEFORE children
   * exist, so gallery/Swiper init fails silently. This function re-runs init
   * once the DOM is connected and renderable.
   *
   * Lazy images use data-srcset (10px placeholder in src). The initial page's
   * LazyLoader (IntersectionObserver) only tracked old images — new ones need
   * to be force-loaded or re-observed.
   */

  function postSwapInit(container) {
    requestAnimationFrame(function () {
      // ── 1. Restore custom element state that was lost during innerHTML parse ──

      // Grid gallery thumbnail — must be fixed BEFORE grid gallery init
      // (grid gallery's actionOutMobile calls thumbnail.bindDesktopActions)
      var gridThumb = container.querySelector('grid-gallery-thumbnail');
      if (gridThumb) {
        gridThumb.actionScroll = gridThumb.querySelectorAll('.action-grid-item');
        gridThumb.MediaGalleryThumbnailHtml = gridThumb.innerHTML;
      }

      // Grid gallery — restore saved HTML, then re-init (sets up Swiper + LazyLoader)
      var gridGallery = container.querySelector('grid-gallery');
      if (gridGallery) {
        gridGallery.MediaGalleryHtml = gridGallery.innerHTML;
        try { gridGallery.init(); } catch (e) { /* non-critical */ }
      }

      // Media gallery — re-init for thumbnail-layout Swiper + Drift zoom
      var gallery = container.querySelector('media-gallery');
      if (gallery && typeof gallery.init === 'function') {
        try { gallery.init(); } catch (e) { /* non-critical */ }
      }

      // ── 2. Force-load all lazy images ──
      // Gallery init may have reset innerHTML (re-introducing data-srcset).
      // Convert all remaining lazy images to eager so nothing stays blurry.

      container.querySelectorAll('.image-lazy-load').forEach(function (img) {
        var picture = img.closest('picture');
        if (picture) {
          var source = picture.querySelector('source');
          if (source && source.dataset.srcset) {
            source.setAttribute('srcset', source.dataset.srcset);
            source.removeAttribute('data-srcset');
          }
        }
        if (img.dataset.srcset) {
          img.setAttribute('srcset', img.dataset.srcset);
          img.removeAttribute('data-srcset');
          img.classList.remove('image-lazy-load');
          img.classList.add('lazy-loaded');
        }
      });

      // Clear loading state on motion-effect wrappers
      container.querySelectorAll('motion-effect.loading').forEach(function (el) {
        el.classList.remove('loading');
        el.classList.add('loaded');
      });
    });
  }

  /* ── AJAX navigation ── */

  function navigateTo(url, clickedEl) {
    S.version++;
    var myVersion = S.version;

    // Immediate visual feedback
    if (clickedEl) {
      var loadingClass = clickedEl.closest('.pack-switcher')
        ? 'pack-switcher__link--loading'
        : 'product-switcher__pill--loading';
      clickedEl.classList.add(loadingClass);
    }

    var currentMain = document.querySelector('#MainContent');
    if (currentMain) currentMain.style.opacity = '0.4';

    var progressBar = document.querySelector('progress-bar-load');
    if (progressBar && typeof progressBar.complete === 'function') {
      progressBar.complete();
    }

    return fetchPage(url)
      .then(function (cached) {
        if (myVersion !== S.version) return;
        if (!cached || !cached.doc) throw new Error('empty');

        var newMain = cached.doc.querySelector('#MainContent');
        if (!newMain || !currentMain) throw new Error('no MainContent');

        hoistStylesheets(newMain);

        currentMain.style.opacity = '';
        currentMain.innerHTML = newMain.innerHTML;

        activateScripts(currentMain);

        // Re-init gallery, lazy images, and Swiper
        postSwapInit(currentMain);

        document.title = cached.title;
        history.pushState({ _ps: true, url: url }, '', url);

        var section = document.querySelector('.sec__main-product')
                   || document.querySelector('#MainContent');
        if (section) {
          var rect = section.getBoundingClientRect();
          var offset = window.pageYOffset + rect.top - 10;
          window.scrollTo({ top: Math.max(0, offset), behavior: 'instant' });
        }

        if (progressBar) {
          progressBar.style.display = 'none';
          var ind = progressBar.querySelector('.indeterminate');
          if (ind) { ind.style.width = '0%'; ind.style.opacity = '1'; }
        }

        // Re-init both switcher types on new content
        initSwitchers();

        try {
          if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
            window.Shopify.analytics.publish('page_viewed');
          }
        } catch (e) { /* non-critical */ }

        document.dispatchEvent(new CustomEvent('product-switcher:navigated', {
          detail: { url: url }
        }));
      })
      .catch(function () {
        if (myVersion !== S.version) return;
        window.location = url;
      });
  }

  /* ── Init: bind events on both switcher types ── */

  function initSwitchers() {
    // Product switcher
    var productSwitcher = document.querySelector('.product-switcher');
    if (productSwitcher) {
      productSwitcher.addEventListener('click', function (e) {
        var pill = e.target.closest('.product-switcher__pill[href]');
        if (!pill) return;
        e.preventDefault();
        navigateTo(pill.getAttribute('href'), pill);
      });
      productSwitcher.addEventListener('mouseover', function (e) {
        var pill = e.target.closest('.product-switcher__pill[href]');
        if (pill) fetchPage(pill.getAttribute('href'), 'high');
      });
      productSwitcher.addEventListener('focusin', function (e) {
        var pill = e.target.closest('.product-switcher__pill[href]');
        if (pill) fetchPage(pill.getAttribute('href'), 'high');
      });
    }

    // Pack switcher
    var packSwitcher = document.querySelector('.pack-switcher');
    if (packSwitcher) {
      packSwitcher.addEventListener('click', function (e) {
        var link = e.target.closest('.pack-switcher__link[href]');
        if (!link) return;
        e.preventDefault();
        navigateTo(link.getAttribute('href'), link);
      });
      packSwitcher.addEventListener('mouseover', function (e) {
        var link = e.target.closest('.pack-switcher__link[href]');
        if (link) fetchPage(link.getAttribute('href'), 'high');
      });
      packSwitcher.addEventListener('focusin', function (e) {
        var link = e.target.closest('.pack-switcher__link[href]');
        if (link) fetchPage(link.getAttribute('href'), 'high');
      });
    }
  }

  /* ── Background prefetch after page load ── */

  function backgroundPrefetch() {
    var urls = getSwitcherUrls();
    if (urls.length === 0) return;

    var delay = 0;
    urls.forEach(function (url) {
      delay += 200;
      setTimeout(function () { fetchPage(url, 'low'); }, delay);
    });
  }

  /* ── Back / forward navigation (register only once) ── */

  if (!S.popstateBound) {
    S.popstateBound = true;

    window.addEventListener('popstate', function (e) {
      var url = location.href;
      if (e.state && e.state._ps) {
        navigateTo(url, null);
      } else if (document.querySelector('.product-switcher') || document.querySelector('.pack-switcher')) {
        location.reload();
      }
    });
  }

  /* ── Boot ── */

  function boot() {
    initSwitchers();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () { backgroundPrefetch(); }, { timeout: 3000 });
    } else {
      setTimeout(backgroundPrefetch, 2000);
    }

    if (!history.state || !history.state._ps) {
      history.replaceState({ _ps: true, url: location.href }, '', location.href);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
